import Immutable from 'immutable';
import { transforms, logMessage, pageContext } from 'ctm-util-functions';
import { reporterLog } from 'ctm-app/client/sagas/reporter';

/**
 * apiResponses form reducers
 * @param {Object} initialState - initial state
 * @returns {Map} - Updated State
 */
export const createApiResponsesReducer = (initialState = Immutable.Map()) => {
    let fuelQuoteFormSchema;
    let fuelApiSchema;

    return (state = initialState, action) => {
        const splitTrafficId = action.splitTrafficId;
        switch (action.type) {
            case 'SET_MANUAL_LOOKUP_DATA': {
                let newState = state;
                if (splitTrafficId === 'redbook_api') {
                    if (typeof state.get('rbtracking') === 'undefined') {
                        newState = state.set('rbtracking', trackRego(action.type, action.splitTrafficId));
                    }
                    return newState;
                }
                if (typeof state.get('mwtracking') === 'undefined') {
                    newState = state.set('mwtracking', trackRego(action.type, action.splitTrafficId));
                }
                return newState;
            }
            case 'SET_MANUAL_LOOKUP_PAYLOAD': {
                return state.setIn(action.identifier.split('.'), Immutable.fromJS(action.payload));
            }
            case 'SET_MANUAL_LOOKUP_COLOURS': {
                const colours = action.payload.map((colour) => {
                    return {
                        code: colour,
                        label: transforms.titleCase({ value: colour })
                    };
                });
                return state.setIn(['colours'], Immutable.fromJS(colours));
            }
            case 'SET_REGO_LOOKUP_PAYLOAD': {
                const {
                    body,
                    colour,
                    fuel,
                    make,
                    model,
                    transmission,
                    year,
                    options,
                    standardFeatures,
                    vehicleDescription,
                    alarmFitted,
                    immobiliserFitted,
                    badge
                } = action.payload;
                const colourInResponse = typeof colour !== 'undefined';
                const apiResponse = {
                    // set it to false so we don't display failed lookup message
                    regoLookupFailed: false,
                    // Wrap in array so it keeps the same data structure with manual look up
                    bodies: [body],
                    colours: colourInResponse ? [colour] : [],
                    fuel: [fuel],
                    makes: [make],
                    models: [model],
                    transmissions: [transmission],
                    badge: [badge],
                    years: [{ code: year, label: year }],
                    // Below are not wrapped in array as manual lookup do not return them at the top layer
                    factoryOptions: options,
                    standardFeatures,
                    description: vehicleDescription,
                    alarmFitted,
                    immobiliserFitted,
                    series: [action.payload]
                };
                if (splitTrafficId === 'redbook_api') {
                    apiResponse.rbtracking = trackRego(action.type, action.splitTrafficId);
                } else {
                    apiResponse.mwtracking = trackRego(action.type, action.splitTrafficId);
                }
                return Immutable.fromJS(apiResponse);
            }
            case 'SET_REGO_LOOKUP_FAIL': {
                let newState;
                if (splitTrafficId === 'redbook_api') {
                    newState = state.set('rbtracking', trackRego(action.type, action.splitTrafficId));
                } else {
                    newState = state.set('mwtracking', trackRego(action.type, action.splitTrafficId));
                }
                // we need this to add some rules so FE can display rego lookup failed message to user
                return newState.set('regoLookupFailed', true);
            }
            case 'SET_MANUAL_LOOKUP_OPTIONS': {
                // After pick the vehicle in manual look up, we need to set selected vehicle data to the top level of the apiResponse for rendering future steps
                const { options, standardFeatures, vehicleDescription, alarmFitted, immobiliserFitted } = action.payload;
                return state.merge({
                    factoryOptions: options,
                    standardFeatures,
                    description: vehicleDescription,
                    alarmFitted,
                    immobiliserFitted
                });
            }
            case 'SET_ROUTING_FOR_EXPERIMENT': {
                const quoteFormModel = pageContext.getProps().models.formSchemaModel;
                const pageSchemaModel = pageContext.getProps().models.pageSchemaModel;
                const carBadgePage = 'car_badge';

                switch (splitTrafficId) {
                    // Each new A/B route will require:
                    //      - a new case for splitTrafficId
                    //      - removal of unused pageModel for default case

                    case 'mvlTest2':
                        logMessage('Experiment ID: mvlTest2 ');

                        fuelQuoteFormSchema = quoteFormModel.getPage('car_fuel');
                        fuelQuoteFormSchema.nextStepButton.autoProgress = false;
                        fuelQuoteFormSchema.nextStepButton.nextPageName = 'car_series';
                        fuelQuoteFormSchema.nextStepButton.elementTouchName = '';

                        fuelApiSchema = pageSchemaModel.getApiSchemaByPageName('car_fuel');
                        fuelApiSchema.onMount.preRequestActions = [
                            {
                                type: 'SET_PAGE_VALUE',
                                identifier: 'showPleaseWait',
                                value: false
                            }
                        ];

                        fuelApiSchema.onMount.responseActions = [
                            {
                                type: 'SET_ROUTING_FOR_EXPERIMENT',
                                identifier: 'experiment_routing'
                            }
                        ];

                        fuelQuoteFormSchema.nextStepButton.rules.props.stateKey = 'vertical.quote.form.coverDetail.vehicle.transmission';

                        break;

                    default:
                    // When there is automatic page progression the page_schema seems to use the next sequential page model declared
                    // in the page_schema.json file.
                    // The pages will automatically progress if only one option is returned from redbook. This routing follows the sequential
                    // declaration of pages in the page_schema.json.
                    // For the purposes of A/B testing with different routes, unused pages must be removed to prevent automatic routing.
                    // E.g. A test (new experiment) goes 1 -> 2 -> 3
                    //      B test (existing flow) goes 1 -> 3
                    //      Remove page 2 from pageSchemaModel for test B (default case).

                    // Leaving these notes here as they will be helpful for future A/B testing with different routes
                    // if (pageSchemaModel.getDictionaryOfSchema()[carBadgePage] === true) {
                    //     pageSchemaModel.removePageByName(carBadgePage);
                    // }
                }
                return state;
            }
            default: {
                return state;
            }
        }
    };
};

const trackRego = (action, splitTrafficId) => {
    const redbookTraffic = splitTrafficId === 'redbook_api';
    const data = {
        event: redbookTraffic ? 'trackRedbook' : 'trackMotorWeb',
        eventCategory: redbookTraffic ? 'car redbook tracking' : 'car motorweb tracking'
    };
    switch (action) {
        case 'SET_REGO_LOOKUP_FAIL': {
            data.eventAction = redbookTraffic ? 'rb fail' : 'mw fail';
            recordGTM(action, data);
            break;
        }
        case 'SET_REGO_LOOKUP_PAYLOAD': {
            data.eventAction = redbookTraffic ? 'rb success' : 'mw success';
            recordGTM(action, data);
            break;
        }
        case 'SET_MANUAL_LOOKUP_DATA': {
            data.eventAction = redbookTraffic ? 'rb not used' : 'mw not used';
            recordGTM(action, data);
            break;
        }
        default:
            break;
    }
    return data.eventAction;
};

const recordGTM = (action, data) => {
    try {
        if (window && window.dataLayer) {
            window.dataLayer.push(data);
            logMessage(`${action} pushed to GTM`, data);
            return;
        }
        // Log an error using the reporter.
        logMessage(action, 'Google Data Layer is undefined');
    } catch (error) {
        // Log the error using the reporter
        reporterLog(action, error);
    }
};
